import React, { useState, useEffect } from 'react';
import EditProfile from './EditProfile';
import {
  FiEdit,
  FiUser,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiMapPin,
  FiHome,
  FiTarget,
} from 'react-icons/fi';
import { FaRegBuilding, FaRegMap } from 'react-icons/fa';
import { CiGlobe } from 'react-icons/ci';
import { toast } from 'react-toastify';
import profileplaceholder from '../../assets/placeholderprofile.png';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  companyType: string;
  website: string;
  country: string;
  city: string;
  address: string;
  postalCode: string;
  profilePhoto: string;
  role?: string;
}

// Typecast icons
const FaRegMapComponent = FaRegMap as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const CiGlobeComponent = CiGlobe as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiTargetComponent = FiTarget as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiEditComponent = FiEdit as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiUserComponent = FiUser as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiMailComponent = FiMail as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiPhoneComponent = FiPhone as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiBriefcaseComponent = FiBriefcase as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FaRegBuildingComponent = FaRegBuilding as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiMapPinComponent = FiMapPin as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiHomeComponent = FiHome as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;

const ProfileView: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    companyType: '',
    website: '',
    country: '',
    city: '',
    address: '',
    postalCode: '',
    profilePhoto: profileplaceholder,
    role: '',
  });
  const [loading, setLoading] = useState(true);

  const getValueOrNA = (value: any): string => value && value.trim() !== '' ? value : 'N/A';

  const fetchProfilePhoto = async (token: string, tenantId: string, userId: string): Promise<string> => {
    try {
      const response = await fetch(`${BASE_URL}/v1/profile/me/photo`, {
        method: 'GET',
        headers: { 'X-Tenant-Id': tenantId, 'X-User-Id': userId || '', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) return profileplaceholder;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return profileplaceholder;
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication token not found');
      setLoading(false);
      return;
    }


    try {
      const response = await fetch(`${BASE_URL}/v1/profile/me`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch profile');

      const result = await response.json();
      const apiData = result.data || {};

      const photoUrl = await fetchProfilePhoto(token, tenantId, userId || '');

      const mappedData: ProfileData = {
        firstName: getValueOrNA(apiData.firstName),
        lastName: getValueOrNA(apiData.lastName),
        email: getValueOrNA(apiData.email),
        phone: getValueOrNA(apiData.phone),
        companyName: getValueOrNA(apiData.companyName),
        companyType: getValueOrNA(apiData.companyType),
        website: getValueOrNA(apiData.website),
        country: getValueOrNA(apiData.country),
        city: getValueOrNA(apiData.city),
        address: getValueOrNA(apiData.address),
        postalCode: getValueOrNA(apiData.postalCode),
        profilePhoto: photoUrl,
        role: getValueOrNA(apiData.role),
      };

      setProfileData(mappedData);
      localStorage.setItem('profileData', JSON.stringify(mappedData));
    } catch (error) {
      // console.error('Profile fetch failed:', error);
      setProfileData({
        ...profileData,
        email: 'N/A', firstName: 'N/A', lastName: 'N/A', phone: 'N/A',
        companyName: 'N/A', companyType: 'N/A', website: 'N/A',
        country: 'N/A', city: 'N/A', address: 'N/A', postalCode: 'N/A',
        role: 'N/A', profilePhoto: profileplaceholder,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleUpdate = (updatedData: ProfileData) => {
    setProfileData(updatedData);
    localStorage.setItem('profileData', JSON.stringify(updatedData));
    setIsEditing(false);
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[400px]"><div className="text-gray-500">Loading profile...</div></div>;
  }

  return (
    <div className='p-6'>
      {!isEditing ? (
        <>
          <div className='bg-gray-50 rounded-lg p-6 flex flex-col items-center relative' style={{ backgroundColor: '#F5F8FA' }}>
            <button onClick={() => setIsEditing(true)} className='absolute top-4 right-4 flex items-center gap-1 text-sky-600 border border-sky-200 rounded px-3 py-2 text-xs hover:bg-sky-50' style={{ color: '#00A4BD', borderColor: '#00A4BD' }}>
              <FiEditComponent /> Edit
            </button>
            <img src={profileData.profilePhoto || profileplaceholder} alt='Profile' className='w-20 h-20 rounded-full object-cover mb-3' onError={(e) => { e.currentTarget.src = profileplaceholder; }} />
            <h3 className='text-lg font-semibold' style={{ color: '#3F5265', fontSize: '20px' }}>{profileData.firstName} {profileData.lastName}</h3>
            <p style={{ color: '#657584', fontSize: '16px' }} className='text-gray text-sm'>{profileData.email}</p>
          </div>

          <div className='mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm px-4 py-2'>
            {/* Personal Details */}
            <div>
              <h4 className='font-semibold mb-6' style={{ color: '#3F5265', fontSize: '18px' }}>Personal Details</h4>
              <ul className='space-y-4 text-gray-600'>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '12px' }}><span className='flex items-center justify-center mr-2' style={{ width: '30px', height: '30px', backgroundColor: '#F5F8FA', borderRadius: '5px', color: 'gray' }}><FiUserComponent size={17} /></span>NAME</span><span>{profileData.firstName} {profileData.lastName}</span></li>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '12px' }}><span className='flex items-center justify-center mr-2' style={{ width: '30px', height: '30px', backgroundColor: '#F5F8FA', borderRadius: '5px', color: 'gray' }}><FiMailComponent size={17} /></span>EMAIL</span><span>{profileData.email}</span></li>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}><span className='flex items-center justify-center mr-2' style={{ width: '30px', height: '30px', backgroundColor: '#F5F8FA', borderRadius: '5px', color: 'gray' }}><FiPhoneComponent size={17} /></span>PHONE NUMBER</span><span>{profileData.phone}</span></li>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}><span className='flex items-center justify-center mr-2' style={{ width: '30px', height: '30px', backgroundColor: '#F5F8FA', borderRadius: '5px', color: 'gray' }}><FiBriefcaseComponent size={17} /></span>ROLE</span><span>{profileData.role}</span></li>
              </ul>
            </div>

            {/* Company Details */}
            <div>
              <h4 className='font-semibold mb-6' style={{ color: '#3F5265', fontSize: '18px' }}>Company Details</h4>
              <ul className='space-y-4 text-gray-600'>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}><span className='flex items-center justify-center mr-2' style={{ width: '30px', height: '30px', backgroundColor: '#F5F8FA', borderRadius: '5px', color: 'gray' }}><FaRegBuildingComponent size={17} /></span>COMPANY</span><span>{profileData.companyName}</span></li>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}><span className='flex items-center justify-center mr-2' style={{ width: '30px', height: '30px', backgroundColor: '#F5F8FA', borderRadius: '5px', color: 'gray' }}><FiTargetComponent size={17} /></span>COMPANY TYPE</span><span>{profileData.companyType}</span></li>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}><span className='flex items-center justify-center mr-2' style={{ width: '30px', height: '30px', backgroundColor: '#F5F8FA', borderRadius: '5px', color: 'gray' }}><CiGlobeComponent size={17} /></span>WEBSITE</span><span>{profileData.website}</span></li>
              </ul>
            </div>

            {/* Location Details */}
            <div>
              <h4 className='font-semibold mb-6' style={{ color: '#3F5265', fontSize: '18px' }}>Location Details</h4>
              <ul className='space-y-4 text-gray-600'>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}><span className='flex items-center justify-center mr-2 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'><FiMapPinComponent size={17} /></span>COUNTRY</span><span>{profileData.country}</span></li>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}><span className='flex items-center justify-center mr-2 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'><FaRegMapComponent size={17} /></span>CITY</span><span>{profileData.city}</span></li>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}><span className='flex items-center justify-center mr-2 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'><FiHomeComponent size={17} /></span>ADDRESS</span><span>{profileData.address}</span></li>
                <li className='flex items-center'><span style={{ color: '#9FA9B2', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}><span className='flex items-center justify-center mr-2 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'><FiMapPinComponent size={17} /></span>POSTAL CODE</span><span>{profileData.postalCode}</span></li>
              </ul>
            </div>
          </div>
        </>
      ) : (
        <EditProfile setIsEditing={setIsEditing} onUpdate={handleUpdate} initialData={profileData} />
      )}
    </div>
  );
};

export default ProfileView;